import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'button',
    'popover',
    'searchView',
    'chatView',
    'messagesContainer',
    'chatInput',
    'searchInput',
    'form',
    'tooltip',
    'sendButton',
    'sendIcon',
    'stopIcon',
    'chatIdField',
  ];

  static values = {
    examId: Number,
    chatId: String,
    summaryUrl: String,
    summaryGenerated: { type: Boolean, default: false },
  };

  connect() {
    // Only initialize if this is the main chat component with required targets
    if (this.hasPopoverTarget && this.hasFormTarget) {
      const startOpen = this.popoverTarget.dataset.startOpen === 'true';

      this.isOpen = startOpen;
      this.isSubmitting = false;

      if (startOpen) {
        this.positionPopover();
      }

      this.handleOutsideClickBound = this.handleOutsideClick.bind(this);
      document.addEventListener('click', this.handleOutsideClickBound);

      // Add direct event listener for Enter key as backup
      if (this.hasChatInputTarget) {
        const inputField = this.chatInputTarget.querySelector('input');
        if (inputField) {
          this.handleKeydownBound = (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.sendMessage(event);
            }
          };
          inputField.addEventListener('keydown', this.handleKeydownBound);
        }
      }
    }
  }

  disconnect() {
    // Only remove event listeners if this component had them
    if (this.hasPopoverTarget && this.hasFormTarget) {
      if (this.handleOutsideClickBound) {
        document.removeEventListener('click', this.handleOutsideClickBound);
      }
      if (this.hasChatInputTarget && this.handleKeydownBound) {
        const inputField = this.chatInputTarget.querySelector('input');
        if (inputField) {
          inputField.removeEventListener('keydown', this.handleKeydownBound);
        }
      }
    }
  }

  togglePopover(event) {
    event.stopPropagation();
    this.isOpen ? this.closePopover() : this.openPopover();
  }

  openPopover() {
    this.popoverTarget.classList.remove('tw-hidden');
    this.isOpen = true;
    this.positionPopover();
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.add('tw-hidden');
      this.tooltipTarget.style.display = 'none';
    }
    // Hide the button when popover opens
    this.buttonTarget.style.visibility = 'hidden';

    if (this.examIdValue && this.summaryUrlValue && !this.summaryGeneratedValue) {
      this.generateSummary();
      this.summaryGeneratedValue = true;
    }

    // Ensure correct view is shown when opening
    this.ensureCorrectView();
  }

  closePopover() {
    this.popoverTarget.classList.add('tw-hidden');
    this.isOpen = false;
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.remove('tw-hidden');
      this.tooltipTarget.style.display = 'block';
    }
    // Show the button when popover closes
    this.buttonTarget.style.visibility = 'visible';
  }

  handleOutsideClick(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.closePopover();
    }
  }

  positionPopover() {
    const button = this.buttonTarget;
    const popover = this.popoverTarget;
    const placement = popover.dataset.placement || 'top-end';

    // Force browser reflow to get correct dimensions
    void popover.offsetHeight;

    const buttonRect = button.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();

    let top = 0;
    let left = 0;
    const gap = 16;

    switch (placement) {
      case 'top-end':
        top = -(popoverRect.height - buttonRect.height);
        left = -(popoverRect.width - buttonRect.width);
        break;
      case 'top-start':
        top = -(popoverRect.height - buttonRect.height);
        left = 0;
        break;
      case 'bottom-end':
        top = buttonRect.height + gap;
        left = -(popoverRect.width - buttonRect.width);
        break;
      case 'bottom-start':
        top = buttonRect.height + gap;
        left = 0;
        break;
    }

    const finalLeft = buttonRect.left + left;

    if (finalLeft < 0) {
      left = -buttonRect.left + 16;
    } else if (finalLeft + popoverRect.width > window.innerWidth) {
      left = window.innerWidth - buttonRect.left - popoverRect.width - 32;
    }

    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
  }

  selectSuggestion(event) {
    if (this.isSubmitting) {
      return;
    }

    const suggestion = event.currentTarget.dataset.suggestion;

    if (this.hasChatInputTarget) {
      const inputField = this.chatInputTarget.querySelector('input');
      if (inputField) {
        inputField.value = suggestion;
        this.sendMessage(event);
        this.scrollToBottom();
      }
    } else if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = suggestion;
      this.performSearch(suggestion);
    }
  }

  // Handle suggestions from message components via document events
  selectSuggestionFromDocument(event) {
    const suggestion = event.target.dataset.suggestion;
    if (suggestion && this.hasSearchInputTarget) {
      this.searchInputTarget.value = suggestion;
      this.performSearch(suggestion);
    }
  }

  handleSearchSubmit(event) {
    if (event?.type === 'keydown' && event.key !== 'Enter') return;
    event?.preventDefault();

    const query = this.searchInputTarget?.value.trim();
    if (query) {
      this.performSearch(query);
    }
  }

  performSearch(query) {
    if (!query?.trim()) return;

    this.showChatView();

    // Set the input value and submit the form
    this.chatInputTarget.value = query;
    this.formTarget.requestSubmit();
  }

  showChatView() {
    this.searchViewTarget?.classList.add('tw-hidden');
    if (this.hasChatViewTarget) {
      this.chatViewTarget.classList.remove('tw-hidden');
      this.chatViewTarget.classList.add('tw-flex');
    }
    this.positionPopover();
  }

  closeChatView() {
    this.chatViewTarget?.classList.add('tw-hidden');
    this.chatViewTarget?.classList.remove('tw-flex');
    this.searchViewTarget?.classList.remove('tw-hidden');
    this.closePopover();
  }

  ensureCorrectView() {
    const startInChat = this.popoverTarget.dataset.startInChat === 'true';
    const hasMessages =
      this.hasMessagesContainerTarget && this.messagesContainerTarget.children.length > 0;

    // If should start in chat OR has messages, show chat view
    if (startInChat || hasMessages) {
      this.showChatView();
    } else {
      // Otherwise show search view
      this.showSearchView();
    }
  }

  showSearchView() {
    if (this.hasSearchViewTarget) {
      this.searchViewTarget.classList.remove('tw-hidden');
    }
    if (this.hasChatViewTarget) {
      this.chatViewTarget.classList.add('tw-hidden');
      this.chatViewTarget.classList.remove('tw-flex');
    }
  }

  resetToSearchView() {
    this.showSearchView();
  }

  sendMessage(event) {
    event.preventDefault();

    if (!this.hasChatInputTarget || this.isSubmitting) {
      return;
    }

    // Find the actual input field within the chatInput target container
    const inputField = this.chatInputTarget.querySelector('input');

    if (!inputField) {
      return;
    }

    const message = inputField.value.trim();
    if (!message) {
      return;
    }

    this.isSubmitting = true;

    const clearInputOnSubmitStart = () => {
      inputField.value = '';
    };

    const resetSubmittingState = () => {
      this.isSubmitting = false;
    };

    this.formTarget.addEventListener('turbo:submit-start', clearInputOnSubmitStart, { once: true });
    this.formTarget.addEventListener('turbo:submit-end', resetSubmittingState, { once: true });

    this.formTarget.requestSubmit();
    this.scrollToBottom();
  }

  scrollToBottom() {
    if (this.hasMessagesContainerTarget) {
      this.messagesContainerTarget.scrollTo({
        top: this.messagesContainerTarget.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  async generateSummary() {
    if (!this.summaryUrlValue) return;
    const response = await fetch(this.summaryUrlValue, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (this.hasChatIdFieldTarget) {
        this.chatIdFieldTarget.value = data.chat_id;
      }
    }
  }
}
